import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Quote, CircleCheck, Star, Sparkles } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const getInitials = (name: string) => {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
};
const testimonials = [{
  id: 1,
  author: "Sarah M.",
  position: "Family Traveler",
  quote: "Our stay at Paradise Palms was magical. The 4-bedroom layout was perfect for our group of 9, and the airport pickup made our arrival in Montego Bay completely stress-free.",
  rating: 5,
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/property_exterior_day.png",
  imageAfter: "https://sacred-kid.10web.cloud/wp-content/uploads/2026/03/image1.jpeg.png?v=n0oqz3b835m",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/avatar_sarah.png"
}, {
  id: 2,
  author: "David K.",
  position: "Beach Enthusiast",
  quote: "The $10 ride to the beach is an absolute steal! The property is stunning, and having a 24hr front desk service gave us such peace of mind throughout our vacation.",
  rating: 5,
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/living_room_view.png",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/bedroom_view.png",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/avatar_david.png"
}, {
  id: 3,
  author: "Elena R.",
  position: "Group Organizer",
  quote: "Crystal clear communication and a breathtaking villa. We loved the different gallery sections for the house. We've already saved our info for next year's rebooking!",
  rating: 5,
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/pool_day.png",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/pool_night.png",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/avatar_elena.png"
}];
const TestimonialSlide = ({
  item,
  index
}: {
  item: (typeof testimonials)[0];
  index: number;
}) => {
  const [showAfter, setShowAfter] = useState(true);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div className="flex flex-col justify-center">
      <Card className="bg-white/80 backdrop-blur-md border-border shadow-xl overflow-hidden relative rounded-[24px]">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        
        <CardContent className="p-8 lg:p-10 relative z-10">
          <div className="flex justify-between items-start mb-6">
            <div className="p-3 bg-primary/10 rounded-xl text-primary">
              <Quote className="size-8 fill-primary/10" />
            </div>
            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 gap-1.5 py-1.5 px-3">
              <CircleCheck className="size-3.5" />
              Verified Guest
            </Badge>
          </div>

          <div className="flex gap-1 mb-6">
            {[...Array(item.rating)].map((_, i) => <Star key={i} className="size-5 fill-[hsl(43,96%,58%)] text-[hsl(43,96%,58%)]" />)}
          </div>

          <blockquote className="text-xl lg:text-2xl font-serif italic font-medium leading-relaxed text-foreground mb-8">
            "{item.quote}"
          </blockquote>

          <div className="flex items-center gap-4 pt-6 border-t border-border">
            <SafeHydrate><Avatar className="size-14 border-2 border-primary/10">
              <AvatarImage src={item.avatar} alt={item.author} />
              <AvatarFallback className="bg-primary/10 text-primary font-bold">
                {getInitials(item.author)}
              </AvatarFallback>
            </Avatar></SafeHydrate>
            <div>
              <div className="font-bold text-lg text-foreground">{item.author}</div>
              <div className="text-muted-foreground text-sm">{item.position}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
};
export default function WhatOurClientsSee() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll();
  return <section id="guest-experiences" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[hsl(43,96%,58%)]/5 rounded-full blur-[100px]" />
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/leaf.png')] opacity-5 mix-blend-multiply"></div>
    </div>

    <div className="container mx-auto px-4 relative z-10">
      <div ref={sectionRef} className={`text-center max-w-3xl mx-auto mb-16 lg:mb-24 transition-all duration-1000 ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
          <Sparkles className="size-4" />
          <span>Guest Experiences</span>
        </div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
          Caribbean Memories
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed">
          Discover why our guests choose Paradise Palms for their Montego Bay escape. Real stories from travelers who found their home away from home.
        </p>
      </div>

      <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full max-w-6xl mx-auto">
        <CarouselContent>
          {testimonials.map((item, index) => <CarouselItem key={item.id} className="basis-full">
              <TestimonialSlide item={item} index={index} />
            </CarouselItem>)}
        </CarouselContent>
        
        <div className="hidden lg:block">
          <CarouselPrevious className="left-[-3rem] bg-white/80 border-border text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all" />
          <CarouselNext className="right-[-3rem] bg-white/80 border-border text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all" />
        </div>
        
        <div className="flex justify-center gap-4 mt-8 lg:hidden">
          <CarouselPrevious className="static translate-y-0 bg-white/80 border-border text-foreground" />
          <CarouselNext className="static translate-y-0 bg-white/80 border-border text-foreground" />
        </div>
      </Carousel>
    </div>
  </section>;
}